
import { Component, Mixins, Watch } from "vue-property-decorator";
import SummaryMixin from "@/views/summary/summary.mixin";
import BaseMixin from "@/views/base/base.mixin";
import UtoxResult from "@/views/summary/utox-result.vue";
import AiInsight from "@/views/summary/ai-insight.vue";
import { PatientSummary, SurveyGroup, SETTINGS, DISPLAY, WatchStatus } from "@/views/summary/summary.types";
import ExpandedPhq9 from "@/views/expanded-dashboard/expanded-phq9.vue";
import ExpandedGad7 from "@/views/expanded-dashboard/expanded-gad7.vue";
import ExpandedSows from "@/views/expanded-dashboard/expanded-sows.vue";
import { SidebarType } from "@/views/sidebar/sidebar.types";
import VueSelect from 'vue-select';

type VueSelectInstance = InstanceType<typeof VueSelect>;

@Component({
  components: {
    UtoxResult,
    AiInsight,
    ExpandedPhq9,
    ExpandedGad7,
    ExpandedSows
  }
})
export default class SummaryComponent extends Mixins(BaseMixin, SummaryMixin) {
  alertVariant: "success" | "danger" = "success";
  participantId = 0;
  observationDate = "";
  firstName = "";
  lastName = "";
  message = "";
  group = new SurveyGroup();
  showHide = DISPLAY.SHOW;
  pickDate = new Date();
  alertVisible = false;
  utoxResultShow = false;
  aiInsightShow = false;
  phq9ExpandedShow = false;
  gad7ExpandedShow = false;
  sowsExpandedShow = false;
  settings = SETTINGS.NONE;
  allSelected = false;
  loading = false;

  get SETTINGS() {
    return SETTINGS;
  }

  get tableFields() {
    const isBulkEditMode =
      this.settings === SETTINGS.EGAGE_PLAN ||
      this.settings === SETTINGS.SHOW_HIDE;

    const showAiInsights =
      Boolean(this.selectedClient.showInsight) &&
      !isBulkEditMode;

    const fields = [ ...this.fields ]
      .filter(field => (field.key !== 'aiInsight' || showAiInsights) 
      && (field.key !== 'watch' || !isBulkEditMode)
      && (field.key !== 'sleep' || this.selectedClientDDL.value === 6));
    if (this.settings === SETTINGS.EGAGE_PLAN) {
      fields.unshift({ key: 'select', label: 'Select' })
    } else if (this.settings === SETTINGS.SHOW_HIDE) {
      fields.unshift({ key: 'show', label: 'show' })
    }
    return fields;
  }

  get selectedLabel(): string {
    const n = this.patientSummaries.filter(item => item.select).length;
    return n === 1
      ? '1 Participant Selected'
      : `${n} Participants Selected`;
  }

  get sortedAndFilteredItems() {
    const query = this.searchQuery.trim().toLowerCase();
    const field = this.sortBy;
    const direction = this.sortDesc ? -1 : 1;

    let items = [...this.patientSummaries];

    if (query) {
      items = items.filter(item => {
        return (
          item.firstName?.toLowerCase().includes(query) ||
          item.lastName?.toLowerCase().includes(query) ||
          item.mrn?.toLowerCase().includes(query) ||
          item.imeiId?.toLowerCase().includes(query)
        );
      });
    }

    items.sort((a, b) => {
      const valA = (a as any)[field];
      const valB = (b as any)[field];

      const isNullA = valA === null || valA === undefined || valA === '';
      const isNullB = valB === null || valB === undefined || valB === '';

      if (isNullA && !isNullB) return 1;
      if (!isNullA && isNullB) return -1;
      if (isNullA && isNullB) return 0;

      // Actual comparison
      if (typeof valA === 'number' && typeof valB === 'number') {
        return direction * (valA - valB);
      }

      if (typeof valA === 'boolean' && typeof valB === 'boolean') {
        return direction * (Number(valA) - Number(valB));
      }

      return direction * String(valA).localeCompare(String(valB));
    });

    return items;
  }

  async mounted() {
    this.loading = true;
    this.selectedPatientSummary = new PatientSummary();
    const client = await this.waitForClientReady();
    if (client) {
      await this.fetchData();
    } else {
      console.warn("Client not ready in time.");
    }
    this.loading = false;
  }

  sort(field: string) {
    if (this.sortBy === field) {
      this.sortDesc = !this.sortDesc;
    } else {
      this.sortBy = field;
      this.sortDesc = false;
    }
  }

  async fetchData() {
    const fetch = [
      this.$store.dispatch("Summary/fetchPhq9ChartOptions", this.selectedClientDDL.value),
      this.$store.dispatch("Summary/fetchGad7ChartOptions", this.selectedClientDDL.value),
      this.$store.dispatch("Summary/fetchSowsChartOptions", this.selectedClientDDL.value),
      this.$store.dispatch("Summary/fetchPatientSummary", {
        clientId: this.selectedClientDDL.value,
        show: this.showHide === DISPLAY.SHOW ? true : false,
        timezone: this.selectedClient.timezone
      }),
      this.$store.dispatch("Summary/fetchSurveyGroups", this.selectedClientDDL.value),
    ];
    await Promise.all(fetch);
  }

  getSortIconClass(field: string, descending: boolean) {
    return {
      'sort-icon-active': this.sortBy === field && this.sortDesc === descending,
      'sort-icon-inactive': !(this.sortBy === field && this.sortDesc === descending)
    };
  }

  getWatchColor(watchStatus: number): string {
    switch (watchStatus) {
      case WatchStatus.NORMAL:
        return '#83b05f';  // up to date
      case WatchStatus.VERSION:
        return '#edb850';  // out of date
      case WatchStatus.NOT_ASSIGNED:
        return '#CCCCCC';  // no watch
      case WatchStatus.NO_DATA:
        return '#f33333';  // no data
      default:
        return '#CCCCCC';  // fallback
    }
  }

  getSleepDurationTitle(duration?: number): string {
    if (duration === undefined || duration === null) {
      return '';
    }
    const dayLabel = duration === 1 ? 'day' : 'days';
    return `Average ${duration} ${dayLabel}`;
  }

  watchStatistics(selectedPatientSummary: PatientSummary) {
    this.selectedPatientSummary = selectedPatientSummary;
    this.$sidebar.open({
      type: SidebarType.WatchStatistics,
      title: this.selectedPatientSummary.watch ? 
        "Engagement Summary" :
        "Watch Assignment",
      useHistory: true,
      item: { showAvatar: true }
    });
  }

  async showOrHide(selectedPatientSummary: PatientSummary) {
    this.loading = true;
    this.settings = SETTINGS.SHOW_HIDE;
    await this.$store.dispatch("Summary/updateParticipantShow", {
      clientId: this.selectedClientDDL.value,
      participantId: selectedPatientSummary.participantId,
      show: !selectedPatientSummary.show,
    });
    if (this.errorMessage) {
      this.message = this.errorMessage;
      this.alertVariant = "danger";
      this.alertVisible = true;
      this.loading = false;
      setTimeout(() => {
        this.alertVisible = false;
      }, 5000);
    } else {
      await this.fetchData();
      this.loading = false;
    }
  }

  goToDashboard(selectedPatientSummary: PatientSummary) {
    this.selectedPatientSummary = selectedPatientSummary;
    this.$router.push({ name: 'DashboardComponent' });
  }

  addParticipant() {
    this.$sidebar.open({
      type: SidebarType.CommercialParticipant,
      title: "Add Participant",
      useHistory: true,
      item: { participantId: -1}
    });
  }

  boxStyle(index: number, value: number) {
    const map: Record<number, string> = {
      1: 'D52121',
      2: 'F4B942',
      3: 'F9DE98',
      4: 'AFE384',
      5: '4C8C2B'
    };
    const hex = map[index];
    return hex && value >= index ? 
      `background: #${hex}` : 
      'background: #E4E1E1';
  }

  showGroupNames(selected: PatientSummary): string {
    if (!selected.groupNames) {
      return "";
    }

    const names = selected.groupNames
      .split(",")
      .map(n => n.trim());

    if (this.settings === SETTINGS.EGAGE_PLAN) {
      return names.includes(this.group.text)
        ? this.group.text
        : "";
    }

    return selected.groupNames;
  }

  showUtoxResult(selectedPatientSummary: PatientSummary) {
    this.selectedPatientSummary = selectedPatientSummary;
    this.utoxResultShow = true;
    this.participantId = selectedPatientSummary.participantId;
    this.observationDate = this.getObservationDate(selectedPatientSummary.participantId);
  }

  async requestAiInsight(selectedPatientSummary: PatientSummary) {
    this.selectedPatientSummary = selectedPatientSummary;
    this.aiInsightShow = true;
    this.participantId = selectedPatientSummary.participantId;
    this.firstName = selectedPatientSummary.firstName;
    this.lastName = selectedPatientSummary.lastName;
    this.observationDate = this.getObservationDate(selectedPatientSummary.participantId);
  }

  showPhq9Expanded(selectedPatientSummary: PatientSummary) {
    this.selectedPatientSummary = selectedPatientSummary;
    this.phq9ExpandedShow = true;
    this.participantId = selectedPatientSummary.participantId;
  }

  showGad7Expanded(selectedPatientSummary: PatientSummary) {
    this.selectedPatientSummary = selectedPatientSummary;
    this.gad7ExpandedShow = true;
    this.participantId = selectedPatientSummary.participantId;
  }

  showSowsExpanded(selectedPatientSummary: PatientSummary) {
    this.selectedPatientSummary = selectedPatientSummary;
    this.sowsExpandedShow = true;
    this.participantId = selectedPatientSummary.participantId;
  }

  toggleAllRows(checked: boolean) {
    this.patientSummaries.forEach(item => item.select = checked)
  }

  async onApplyEGagePlan() {
    this.loading = true;

    const participantIds = this.patientSummaries
      .filter(p => p.select)
      .map(p => Number(p.participantId));

    await this.$store.dispatch("Summary/addSurveyGroupParticipantMap", {
      clientId: this.selectedClientDDL.value,
      participantIds: participantIds,
      groupId: this.group.value,
    });

    if (this.errorMessage) {
      this.message = this.errorMessage;
      this.alertVariant = "danger";
    } else {
      this.message = "Engagement plan updated successfully!";
      this.alertVariant = "success";
    }

    this.alertVisible = true;
    this.settings = SETTINGS.NONE;
    this.group = new SurveyGroup();
    await this.fetchData();
    this.loading = false;
    this.alertVisible = true;
    setTimeout(() => {
      this.alertVisible = false;
    }, 5000);
  }

  async onCancel() {
    this.loading = true;
    this.settings = SETTINGS.NONE;
    this.showHide = DISPLAY.SHOW;
    this.group = new SurveyGroup();
    await this.fetchData();
    this.loading = false;
  }

  async showEGagePlan() {
    this.loading = true;
    this.settings = SETTINGS.EGAGE_PLAN;
    this.group = new SurveyGroup();
    await this.fetchData();
    this.loading = false;
  }

  async showShowHide() {
    this.loading = true;
    this.settings = SETTINGS.SHOW_HIDE;
    await this.fetchData();
    this.loading = false;
  }

  handleBlur(refName: string): void {
    const selectComponent = this.$refs[refName] as VueSelectInstance
    const input = selectComponent?.$el?.querySelector('input')
    input?.blur()
  }

  @Watch("$sidebar.options")
  async onReload() {
    if (this.$sidebar.options?.reload) {
      this.loading = true;
       this.settings = SETTINGS.NONE;
      await this.fetchData();
      this.loading = false;
    }
  }

  @Watch("selectedClientDDL")
  async onClientChange() {
    this.loading = true;
    this.settings = SETTINGS.NONE;
    await this.fetchData();
    this.loading = false;
  }

  @Watch('group')
  onGroupChange(newGroup: SurveyGroup) {
    this.patientSummaries.forEach(item => {
      if (!item.groupNames) {
        item.select = false;
        return;
      }

      const names = item.groupNames
        .split(',')
        .map(g => g.trim().toLowerCase());

      item.select = names.includes(newGroup.text.toLowerCase());
    });

    this.allSelected = this.patientSummaries.every(item => item.select);
  }

  @Watch('showHide')
  async onShowHideChange() {
    this.loading = true;
    await this.fetchData();
    this.loading = false;
  }
}
